import { useEffect, useRef, useState } from 'react';
import { filterCandidates, getMentionQuery, insertMention, type MentionCandidate } from '../lib/mentions';

interface Props {
  value: string;
  onChange: (value: string) => void;
  candidates: MentionCandidate[];
  placeholder?: string;
  rows?: number;
  className?: string;
  style?: React.CSSProperties;
  /** Ctrl/⌘+Enter sends. */
  onSubmit?: () => void;
  autoFocus?: boolean;
  /** Bump to focus the end of the text and open the picker (an "@" button). */
  focusSignal?: number;
}

/** Textarea where typing @ suggests people to tag. */
export function MentionTextarea({
  value,
  onChange,
  candidates,
  placeholder,
  rows = 2,
  className = '',
  style,
  onSubmit,
  autoFocus,
  focusSignal = 0,
}: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = useState<{ query: string; start: number } | null>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!focusSignal) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(value.length, value.length);
    refreshQuery(value, value.length);
    // Only when asked, not on every keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSignal]);

  const matches = query ? filterCandidates(candidates, query.query) : [];
  const open = matches.length > 0;

  function refreshQuery(text: string, caret: number) {
    const next = getMentionQuery(text, caret);
    setQuery(next);
    setActive(0);
  }

  function choose(candidate: MentionCandidate) {
    const el = ref.current;
    if (!el || !query) return;
    const { text, caret } = insertMention(value, query.start, el.selectionStart, candidate.name);
    onChange(text);
    setQuery(null);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (open) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => (i + 1) % matches.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => (i - 1 + matches.length) % matches.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); choose(matches[active]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setQuery(null); return; }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  }

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        rows={rows}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={className}
        style={style}
        onChange={e => {
          onChange(e.target.value);
          refreshQuery(e.target.value, e.target.selectionStart);
        }}
        onKeyDown={handleKeyDown}
        onClick={e => refreshQuery(value, e.currentTarget.selectionStart)}
        onBlur={() => setTimeout(() => setQuery(null), 150)}
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {open && (
        <ul
          role="listbox"
          className="absolute left-0 bottom-full mb-1 z-30 w-64 max-h-56 overflow-y-auto rounded-xl bg-white shadow-lg ring-1 ring-black/5 py-1 text-left"
        >
          {matches.map((c, i) => (
            <li key={c.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseDown={e => { e.preventDefault(); choose(c); }}
                onMouseEnter={() => setActive(i)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-800 ${i === active ? 'bg-gray-100' : ''}`}
              >
                <span className="w-6 h-6 rounded-full bg-[#15140F] text-[#D6FF4B] text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                  {c.name.charAt(0).toUpperCase()}
                </span>
                <span className="truncate flex-1">{c.name}</span>
                {c.hint && <span className="text-[10px] text-gray-400">{c.hint}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Comment text with tagged names highlighted. */
export function MentionText({ parts, highlightClassName = 'font-semibold text-[var(--ch-blue)]', highlightStyle }: {
  parts: { text: string; mention: boolean }[];
  highlightClassName?: string;
  highlightStyle?: React.CSSProperties;
}) {
  return (
    <>
      {parts.map((p, i) => p.mention
        ? <span key={i} className={highlightClassName} style={highlightStyle}>{p.text}</span>
        : <span key={i}>{p.text}</span>)}
    </>
  );
}
